/**
 * Feb 2027 · Strategic Goal activation helpers.
 *
 * A "Strategic Goal" is activated in Operating Mode when at least
 * one Project (experiment) has that goal's id in its `goal_id`
 * field. Un-activated strategic goals are excluded from the
 * launch/operating readiness score (per founder decision — the
 * score should reflect measurable, in-flight work only).
 *
 * The synthetic monthly-profit goal (`goal_monthly_profit`) is
 * always treated as activated — it is system-managed and never
 * needs a Project attached.
 *
 * These helpers are safe to call for launching-mode businesses:
 * they simply return all goals unchanged (no activation check
 * applied).
 */

export function isGoalActivated(goal, experiments = []) {
  if (!goal) return false;
  if (goal.id === "goal_monthly_profit") return true;
  // Feb 2027 · Multi-goal projects — a project counts as activating
  // this goal when the goal id appears in either `goal_ids` (source
  // of truth) or the legacy single `goal_id` field.
  return (experiments || []).some((e) => {
    if (!e) return false;
    if (Array.isArray(e.goal_ids) && e.goal_ids.length) {
      return e.goal_ids.includes(goal.id);
    }
    return e.goal_id === goal.id;
  });
}

export function isOperatingBusiness(business) {
  return (business?.mode || "launching") === "operating";
}

/**
 * Filter a business's goals to the "scored" subset — activated
 * strategic goals in Operating Mode, all goals in Launching Mode.
 */
export function scoredGoals(business) {
  const goals = business?.goals || [];
  if (!isOperatingBusiness(business)) return goals;
  const experiments = business?.experiments || [];
  return goals.filter((g) => isGoalActivated(g, experiments));
}

/**
 * Count of goals that are excluded from the score in Operating
 * Mode because no Project is attached yet. Returns 0 for
 * launching-mode businesses.
 */
export function unactivatedGoalCount(business) {
  if (!isOperatingBusiness(business)) return 0;
  const goals = business?.goals || [];
  const experiments = business?.experiments || [];
  return goals.filter((g) => !isGoalActivated(g, experiments)).length;
}
